import { nameKey, normalizeVariableName } from './names';
import {
  VARIABLE_TYPE_ARRAY,
  isArrayVariable,
  normalizeArrayValues,
} from './types';
import { clearVariableRowValues, countFilledVariableSlots } from './values';

const stripInheritedDisplayFields = (row) => {
  if (!row || typeof row !== 'object') return row;
  const next = { ...row };
  delete next.scope;
  delete next.sourceId;
  delete next.sourceName;
  delete next.shadowed;
  return next;
};

/** A campaign row that only stores this campaign's value for a doc variable. */
export const isDocValueOverlay = (row) => !!row?.docOverlay;

/** Campaign definitions. Doc value overlays are not campaign variables. */
export const campaignDefinedVariables = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((row) => row && !isDocValueOverlay(row));

/**
 * Copy a doc-default row onto the current campaign (or patch the existing
 * campaign row of the same name). Keeps the doc id so linked-variable-row
 * bindings stay attached. The copy is a value overlay (`docOverlay`) — it
 * stays a doc variable everywhere it is shown. New claims are unlocked so
 * Reset Campaign can fall back to the shared default.
 *
 * A campaign variable that already owns the name under a different id is
 * left as a campaign variable.
 */
export const claimDocVariableOntoCampaign = (
  campaignVariables,
  docVariable,
  patch = {}
) => {
  const name = normalizeVariableName(docVariable?.name);
  const rows = Array.isArray(campaignVariables) ? campaignVariables : [];
  if (!name) return rows;

  const existing = rows.find((v) => nameKey(v.name) === nameKey(name));
  const base = existing
    ? stripInheritedDisplayFields(existing)
    : stripInheritedDisplayFields({
        id: docVariable.id,
        name: docVariable.name,
        value: docVariable.value ?? '',
        description: docVariable.description,
        color: docVariable.color,
        type: docVariable.type,
        values: Array.isArray(docVariable.values)
          ? [...docVariable.values]
          : docVariable.values,
        locked: false,
      });
  const merged = stripInheritedDisplayFields({ ...base, ...patch });
  delete merged.docOverlay;
  if (!existing) merged.name = name;
  if (!existing || existing.docOverlay || existing.id === docVariable.id) {
    merged.docOverlay = true;
  }

  if (existing) {
    return rows.map((v) => (v.id === existing.id ? merged : v));
  }
  return [...rows, merged];
};

/**
 * Mark campaign rows whose id still lives on an attached doc. Those rows
 * are value copies of doc variables, saved before `docOverlay` existed.
 */
export const stampDocOverlays = (campaignVariables, docVariableLists = []) => {
  const docIds = new Set();
  for (const list of docVariableLists) {
    for (const row of list || []) {
      if (row?.id) docIds.add(row.id);
    }
  }
  const rows = Array.isArray(campaignVariables) ? campaignVariables : [];
  if (docIds.size === 0) return { variables: rows, changed: false };
  let changed = false;
  const variables = rows.map((row) => {
    if (!row || row.docOverlay || !docIds.has(row.id)) return row;
    changed = true;
    return { ...row, docOverlay: true };
  });
  return { variables, changed };
};

/**
 * Campaign or mission row that stores a value for a doc variable.
 * Name match wins. Same id is the fallback so renaming the doc variable
 * does not detach the value copy.
 */
export const findCampaignOverlay = (campaignVariables, docVariable) => {
  const rows = campaignVariables || [];
  const key = nameKey(docVariable?.name);
  if (key) {
    const byName = rows.find((v) => nameKey(v.name) === key);
    if (byName) return byName;
  }
  if (!docVariable?.id) return null;
  return rows.find((v) => v && v.id === docVariable.id) || null;
};

/** Drop value copies of one doc variable. Real campaign variables stay. */
export const removeDocValueOverlays = (rows, docVariable) => {
  const id = docVariable?.id;
  const key = nameKey(docVariable?.name);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!row) return false;
    if (id && row.id === id) return false;
    if (row.docOverlay && key && nameKey(row.name) === key) return false;
    return true;
  });
};

/** Paint a doc row with this campaign's value overlay, if any. */
export const applyCampaignOverlayToDocRow = (docRow, overlay) => {
  if (!docRow || !overlay) return docRow;
  const next = { ...docRow, value: overlay.value };
  if (Array.isArray(overlay.values)) next.values = [...overlay.values];
  if (overlay.type) next.type = overlay.type;
  return next;
};

/**
 * Empty what the user sees in a campaign doc: wipe unlocked campaign-only
 * values, and claim empty overlays over unlocked filled doc defaults so
 * this campaign goes blank without rewriting the shared doc.
 */
export const emptyCampaignViewVariableValues = (
  campaignVariables = [],
  docVariables = []
) => {
  const docs = Array.isArray(docVariables) ? docVariables : [];
  const lockedDocKeys = new Set(
    docs.filter((v) => v?.locked).map((v) => nameKey(v.name)).filter(Boolean)
  );
  let next = (Array.isArray(campaignVariables) ? campaignVariables : []).map(
    (row) => {
      if (!row || row.locked) return row;
      if (lockedDocKeys.has(nameKey(row.name))) return row;
      return clearVariableRowValues(row);
    }
  );
  for (const doc of docs) {
    if (!doc || doc.locked) continue;
    const overlay = findCampaignOverlay(next, doc);
    const shown = applyCampaignOverlayToDocRow(doc, overlay);
    if (countFilledVariableSlots(shown) === 0) continue;
    next = claimDocVariableOntoCampaign(next, doc, {
      value: '',
      ...(isArrayVariable(shown)
        ? {
            type: VARIABLE_TYPE_ARRAY,
            values: normalizeArrayValues(shown.values).map(() => ''),
          }
        : {}),
    });
  }
  return next;
};

export const countDisplayedFilledSlots = (docVariables = [], campaignVariables = []) => {
  const docs = Array.isArray(docVariables) ? docVariables : [];
  const camps = Array.isArray(campaignVariables) ? campaignVariables : [];
  const docKeys = new Set(docs.map((v) => nameKey(v.name)).filter(Boolean));
  let n = 0;
  for (const row of camps) {
    if (!row || row.locked || docKeys.has(nameKey(row.name))) continue;
    n += countFilledVariableSlots(row);
  }
  for (const doc of docs) {
    if (!doc || doc.locked) continue;
    n += countFilledVariableSlots(
      applyCampaignOverlayToDocRow(doc, findCampaignOverlay(camps, doc))
    );
  }
  return n;
};

export const hasDisplayedFilledVariableValues = (
  docVariables = [],
  campaignVariables = []
) => countDisplayedFilledSlots(docVariables, campaignVariables) > 0;
